import { supabase } from './api'

/**
 * Headlines — the EOS / 90.io agenda item.
 *
 * A headline is one line of news read out at the L10 meeting. It is not a task
 * and not an issue: anything that needs discussing gets "dropped down" to the
 * issues list, which is what `makeIssueFromHeadline` in the UI does.
 *
 * Everyone posts and everyone reads. Marking one shared, archiving it, and
 * dropping it down are admin-only and re-checked in the database.
 *
 * See 0012_headlines.sql. Deliberately no notification of any kind — see the
 * migration header for why.
 */

/** Matches the cap enforced by `create_headline`. Keep the two in step. */
export const HEADLINE_MAX = 280

export interface Headline {
  id: string
  body: string
  created_by_id: string | null
  created_by_name: string
  /** True when the signed-in person shared it. */
  mine: boolean
  created_at: string
  /**
   * Days since it was shared, computed by Postgres in America/New_York — the
   * same rule as tasks' `days_until`. Never recompute this in the browser.
   */
  days_open: number
  /** Read out at a meeting. The whole lifecycle is this one flag. */
  shared: boolean
  shared_at: string | null
  archived: boolean
  /** Set once it has been dropped down to the issues list. */
  issue_id: string | null
  issue_title: string | null
  /** Set once it has also been turned into a to-do (0019). */
  task_id: string | null
  task_title: string | null
}

function fail(error: { code?: string; message?: string }): Error {
  if (error.code === '28000') {
    return new Error('This link is not recognised. Ask Scott for a new one.')
  }
  if (error.code === '42501') {
    return new Error(error.message || 'You do not have permission to do that.')
  }
  if (error.code === '22023') {
    return new Error(error.message || 'That value is not allowed.')
  }
  return new Error(error.message || 'Something went wrong. Try again.')
}

export async function listHeadlines(
  token: string,
  includeArchived = false,
): Promise<Headline[]> {
  const { data, error } = await supabase.rpc('list_headlines', {
    p_token: token,
    p_include_archived: includeArchived,
  })
  if (error) throw fail(error)
  return (data as Headline[] | null) ?? []
}

export async function createHeadline(token: string, body: string): Promise<string> {
  const { data, error } = await supabase.rpc('create_headline', {
    p_token: token,
    p_body: body,
  })
  if (error) throw fail(error)
  return data as string
}

/** Fix a headline. The author or an admin; the database enforces which. */
export async function updateHeadline(
  token: string,
  headlineId: string,
  body: string,
): Promise<void> {
  const { error } = await supabase.rpc('update_headline', {
    p_token: token,
    p_headline_id: headlineId,
    p_body: body,
  })
  if (error) throw fail(error)
}

export async function adminSetHeadlineShared(
  token: string,
  headlineId: string,
  shared: boolean,
): Promise<void> {
  const { error } = await supabase.rpc('admin_set_headline_shared', {
    p_token: token,
    p_headline_id: headlineId,
    p_shared: shared,
  })
  if (error) throw fail(error)
}

export async function adminArchiveHeadline(
  token: string,
  headlineId: string,
  archived: boolean,
): Promise<void> {
  const { error } = await supabase.rpc('admin_archive_headline', {
    p_token: token,
    p_headline_id: headlineId,
    p_archived: archived,
  })
  if (error) throw fail(error)
}

